import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GetUserInfoService from "../../../../../../service/GetUserInfoService";
import localeText from "../../../../../../locale";
import { formatDate } from "../../../../../../utilities/formatDate";
import { formatPrice } from "../../../../../../utilities/formatPrice";

function formatDateYYYYMMDD(date) {
  return (
    date.getUTCFullYear() +
    "-" +
    String(date.getUTCMonth() + 1).padStart(2, "0") +
    "-" +
    String(date.getUTCDate()).padStart(2, "0")
  );
}

const statusConfig = {
  COMPLETED: { text: "completed", color: "var(--theme-primary, #1DAF61)" },
  PENDING: { text: "pending", color: "#F7B005" },
  PENDINGVOUCHER: { text: "pendingVoucher", color: "#F7B005" },
  CANCELED: { text: "canceled", color: "#eb4034" },
};

const ExtracreditMiniStatement = ({ currency }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const getUserInfoService = new GetUserInfoService();
  const token = localStorage.getItem("__extraconomy_token");
  const username = localStorage.getItem("__extraconomy_username");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const dateBegin = formatDateYYYYMMDD(new Date("2000-01-01"));
        const dateEnd = formatDateYYYYMMDD(new Date());
        const res = await getUserInfoService.getExtracoinTransactions(
          token,
          username,
          dateBegin,
          dateEnd,
        );
        if (!cancelled && (res.messageCode === 200000 || res.messageCode === 200001)) {
          const list = res.result || [];
          setTransactions(list.slice(0, 3));
        } else {
          setTransactions([]);
        }
      } catch {
        if (!cancelled) setTransactions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token, username]);

  return (
    <div className="mb-4">
      <div
        className="card border-round-md p-3 overflow-hidden"
        style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
      >
        {loading ? (
          <div
            className="flex justify-content-center py-5"
            style={{ color: "var(--theme-text-muted, #7A869A)" }}
          >
            <i
              className="pi pi-spin pi-spinner text-2xl"
              style={{ color: "var(--theme-text-muted, #7A869A)" }}
            />
          </div>
        ) : transactions.length === 0 ? (
          <div
            className="flex flex-column justify-content-center align-items-center gap-2"
            style={{ color: "var(--theme-text-muted, #7A869A)", minHeight: "150px" }}
          >
            {localeText("noOperationsRecords") || "Ainda não há registros de operações."}
          </div>
        ) : (
          <div className="flex flex-column">
            <div className="flex justify-content-between align-items-center">
              <div className="font-bold m-0" style={{ color: "var(--theme-text, #172B4D)" }}>
                {localeText("creditMovement")}
              </div>
              <Link
                to="/dashboard-extracoinStatement"
                className="text-sm p-no-outline border-none bg-transparent cursor-pointer hover:underline"
                style={{ color: "var(--theme-primary, #1DAF61)" }}
              >
                {localeText("completeStatement") || "Extrato completo"}
              </Link>
            </div>
            <hr className="mt-3 mb-0" style={{ borderColor: "var(--theme-border)" }} />
            {transactions.map((row, index) => {
              const statusObj = statusConfig[row.status] ?? null;
              const isLast = index === transactions.length - 1;
              const inbound = row.income ?? 0;
              const outbound = row.outcome ?? 0;
              const hasInbound = inbound > 0;
              const value = hasInbound ? inbound : outbound;
              const sign = hasInbound ? "+" : "-";

              return (
                <div
                  key={row.id ?? row.date ?? Math.random()}
                  className="flex align-items-center justify-content-between"
                  style={
                    isLast ? undefined : { borderBottom: "1px solid var(--theme-border, #EBEBEB)" }
                  }
                >
                  <div
                    className={`flex align-items-center gap-3 flex-1 min-w-0 ${isLast ? "pb-0 pt-3" : "py-3"}`}
                  >
                    <div
                      className="flex align-items-center justify-content-center border-round"
                      style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: hasInbound
                          ? "color-mix(in srgb, var(--theme-primary) 15%, transparent)"
                          : "color-mix(in srgb, var(--theme-secondary) 15%, transparent)",
                      }}
                    >
                      <i
                        className={`pi ${hasInbound ? "pi-arrow-down" : "pi-shopping-cart"} text-lg`}
                        style={{
                          color: hasInbound
                            ? "var(--theme-primary, #1DAF61)"
                            : "var(--theme-secondary, #F7B005)",
                        }}
                      />
                    </div>
                    <div className="min-w-0">
                      <div
                        className="font-medium truncate text-sm lowercase"
                        style={{ color: "var(--theme-text, #171717)" }}
                      >
                        {row.description}
                      </div>
                      {(row.reference || row.sender) && (
                        <div className="text-sm text-500">{row.reference || row.sender}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex align-items-center gap-2 flex-shrink-0">
                    <span
                      className="text-sm"
                      style={{
                        color: hasInbound
                          ? "var(--theme-primary, #1DAF61)"
                          : "var(--theme-text, #171717)",
                        fontWeight: 600,
                      }}
                    >
                      {sign} {currency} {formatPrice(value)} ECR
                    </span>
                    <div className="flex align-items-center gap-2">
                      <span className="text-sm text-500">
                        {row.date ? formatDate(row.date) : ""}
                      </span>
                      <Link
                        to="/dashboard-extracoinStatement"
                        className="text-500"
                        style={{ color: "var(--theme-text-muted, #7A869A)" }}
                        aria-label={localeText("seeDetails")}
                      >
                        <i className="pi pi-chevron-right" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExtracreditMiniStatement;
